import type { Specimen, Valuation } from '@numistic/contracts'
import { useCreateValuation, useMeta, useProfile, useRemoveValuation } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Select,
} from '@numistic/ui'
import { useState } from 'react'

import { MutationErrors } from '../components/mutation-errors.js'
import { useErrorText } from '../lib/errors.js'
import { formatDate, formatMoney, localToday } from '../lib/format.js'

const today = localToday
const asDate = (iso: string) => new Date(`${iso}T00:00:00`)

/** История оценок стоимости экземпляра с графиком в базовой валюте. */
export function ValuationsBox({ specimen }: { specimen: Specimen }) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const meta = useMeta().data
  const base = useProfile().data?.baseCurrency ?? 'RUB'
  const create = useCreateValuation(specimen.id)
  const remove = useRemoveValuation()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState({ valuedOn: today(), amount: '', currency: base, source: '' })
  const valuations = specimen.valuations

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2">
        <CardTitle className="mr-auto">{t('valuations.title')}</CardTitle>
        <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)}>
          {t('valuations.add')}
        </Button>
      </CardHeader>
      <CardContent className="grid gap-3">
        <MutationErrors errors={[remove.error]} />
        {open ? (
          <form
            className="grid gap-3 rounded-lg border p-3 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault()
              const amount = draft.amount.replace(',', '.').replace(/\s/g, '')
              if (!amount) return
              create.mutate(
                {
                  valuedOn: draft.valuedOn,
                  amount,
                  currency: draft.currency,
                  source: draft.source.trim() || null,
                },
                {
                  onSuccess: () => {
                    setOpen(false)
                    setDraft((d) => ({ ...d, amount: '', source: '' }))
                  },
                },
              )
            }}
          >
            <Field label={t('valuations.date')}>
              <Input
                type="date"
                value={draft.valuedOn}
                onChange={(e) => setDraft({ ...draft, valuedOn: e.target.value })}
                required
              />
            </Field>
            <Field label={t('valuations.amount')}>
              <Input
                inputMode="decimal"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                required
              />
            </Field>
            <Field label={t('specimen.currency')}>
              <Select
                value={draft.currency}
                onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
              >
                {(meta?.currencies ?? []).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('valuations.source')}>
              <Input
                value={draft.source}
                placeholder={t('valuations.sourcePlaceholder')}
                onChange={(e) => setDraft({ ...draft, source: e.target.value })}
              />
            </Field>
            {create.error ? (
              <Alert variant="destructive" className="sm:col-span-2">
                {errorText.request(create.error)}
              </Alert>
            ) : null}
            <Button type="submit" className="justify-self-start">
              {t('common.save')}
            </Button>
          </form>
        ) : null}
        {valuations.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('valuations.empty')}</p>
        ) : null}
        <ValueChart valuations={valuations} />
        <ol className="grid gap-2">
          {[...valuations].reverse().map((v) => (
            <li
              key={v.id}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-l-2 border-primary/40 pl-3 text-sm"
            >
              <span className="text-muted-foreground">{formatDate(asDate(v.valuedOn))}</span>
              <span className="font-medium">{formatMoney(v.amount, v.currency)}</span>
              <BaseHint amount={v.amountBase} base={v.baseCurrency} currency={v.currency} />
              {v.source ? <span className="text-muted-foreground">{v.source}</span> : null}
              <button
                type="button"
                className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                onClick={() => remove.mutate(v.id)}
              >
                {t('common.remove')}
              </button>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  )
}

/** «≈ 9 000 ₽» — сумма в базовой валюте, если она отличается от исходной. */
export function BaseHint({
  amount,
  base,
  currency,
}: {
  amount: string | null
  base: string | null
  currency: string | null | undefined
}) {
  const { t } = useTranslation()
  if (!base || base === currency) return null
  if (amount === null)
    return <span className="text-xs text-muted-foreground">{t('valuations.noRate')}</span>
  return <span className="text-muted-foreground">≈ {formatMoney(amount, base)}</span>
}

/** Простой линейный график оценок (в базовой валюте); нужен минимум две точки. */
function ValueChart({ valuations }: { valuations: Valuation[] }) {
  const { t } = useTranslation()
  const points = valuations
    .filter((v) => v.amountBase !== null)
    .map((v) => ({ x: asDate(v.valuedOn).getTime(), y: Number(v.amountBase), v }))
  if (points.length < 2) return null
  const base = points[0]!.v.baseCurrency!
  const [w, h, pad] = [320, 120, 8]
  const xs = points.map((p) => p.x)
  const ys = points.map((p) => p.y)
  const [minX, maxX] = [Math.min(...xs), Math.max(...xs)]
  const [minY, maxY] = [Math.min(...ys), Math.max(...ys)]
  const sx = (x: number) => pad + ((x - minX) / (maxX - minX || 1)) * (w - pad * 2)
  const sy = (y: number) => h - pad - ((y - minY) / (maxY - minY || 1)) * (h - pad * 2)
  const line = points.map((p) => `${sx(p.x)},${sy(p.y)}`).join(' ')
  return (
    <figure className="grid gap-1">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="h-32 w-full text-primary"
        role="img"
        aria-label={t('valuations.chart')}
      >
        <polyline points={line} fill="none" stroke="currentColor" strokeWidth="2" />
        {points.map((p) => (
          <circle key={p.v.id} cx={sx(p.x)} cy={sy(p.y)} r="3" fill="currentColor">
            <title>
              {formatDate(asDate(p.v.valuedOn))}: {formatMoney(p.y, base)}
            </title>
          </circle>
        ))}
      </svg>
      <figcaption className="flex justify-between text-xs text-muted-foreground">
        <span>{formatMoney(minY, base)}</span>
        <span>{formatMoney(maxY, base)}</span>
      </figcaption>
    </figure>
  )
}
